/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type NextApiRequest } from 'next';
import {
  type ContentStackLandingPages,
  type FormattedContentStackLandingPage,
} from '../../interface';
import { getContentByContentType } from '../common/getContentByContentType';
import { type PageContextType } from '@/next-types';
import { type ParsedUrlQuery } from 'querystring';
import { deepSegmentationFilter } from '../../utils/deepSegmentationFilter';
import { countryCodes } from '@/constants/locale';

export const LANDING_PAGE_FIELDS = [
  'faq.faq',
  'banner.banner',
  'banner_split.banner_split',
  'ymme.ymme',
  'image_category_grid.image_category_grid',
  'banner_cta.banner_cta',
  'logo_description.logo_description',
  'menu_list.menu_list',
  'category_grid.category_grid',
  'image_content.image_content',
  'image_content_background.image_content_background',
  'parttype_grid.parttype_grid',
  'product_grid.product_grid',
  'prefooter.prefooter',
  'blogtype_grid.blogtype_grid',
  'rich_text_editor.rich_text_editor',
  'featured_content.featured_content',
  'video_content.video_content',
  'product_chart.product_chart',
  'button_grid.button_grid',
  'target_div.target_div',
  'experience_injector.experience_injector',
  'image_video_split.image_video_split',
  'heading.heading',
  'icon_grid.icon_grid',
  'circular_pdf.circular_pdf',
  'super_duper_hero.super_duper_hero',
  'live_text_hero.live_text_hero',
  'feature_carousel.feature_carousel',
  'certona.certona',
  'grid_banner.grid_banner',
  'terms_conditions.terms_conditions',
  'iframe.iframe',
  'deals_split.deals_split',
  'pencil_banner.pencil_banner',
];

export const getContentStackLandingPageContent = async (
  req: NextApiRequest | PageContextType['req'],
  query: ParsedUrlQuery,
  userSegments: string[],
  languageOverride: string | null,
): Promise<FormattedContentStackLandingPage> => {
  const response = await getContentByContentType<ContentStackLandingPages>({
    contentTypeUid: 'landing_pages',
    referenceFieldPath: [
      ...LANDING_PAGE_FIELDS.map((field) => `modular_blocks.${field}`),
      'bottom_modular_blocks.rich_text_toc.rich_text_toc',
    ],
    req,
    query,
    languageOverride,
    customizeQuery: ({ query, language, queryParams }) => {
      if (
        'pageName' in queryParams &&
        queryParams.pageName !== '' &&
        typeof queryParams.pageName === 'string'
      ) {
        const basePath = language === countryCodes.es_US.toLowerCase() ? '/es/lp/' : '/lp/';
        query.where('url', `${basePath}${queryParams.pageName}`);
      }

      return query;
    },
  });
  let modularBlocks = response.modular_blocks;

  if (userSegments.length) {
    modularBlocks = deepSegmentationFilter(modularBlocks, userSegments);
  }

  return {
    modularBlocks,
    bottomModularBlocks: response.bottom_modular_blocks,
    url: response.url,
    seoTags: response.seo,
    landingPageHeading: response.landingpage_heading,
  };
};
